import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import api, { formatFCFA, deliveryLabel, paymentLabel, resolveImage } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Check, CreditCard, Wallet, Truck, Store, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STEPS = [
  { id: 1, label: "Coordonnées" },
  { id: 2, label: "Livraison" },
  { id: 3, label: "Paiement" },
];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryCfg, setDeliveryCfg] = useState({
    restaurant_name: "Dakar Street Food — Restaurant",
    restaurant_address: "2923 Walnut Bend Ln, Houston, TX 77042",
    restaurant_phone: "(281) 846-6420",
    restaurant_hours: "Mon–Sun · 11AM–10PM",
    foodtruck_name: "Dakar Street Food — Food Truck",
    foodtruck_address: "Houston · TX (location varies daily)",
    foodtruck_phone: "(281) 846-6420",
    foodtruck_hours: "Lunch hours · Track via Instagram",
  });

  useEffect(() => {
    api.get("/delivery/locations").then((r) => setDeliveryCfg(r.data)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    city: "Brooklyn",
    delivery_method: "pickup_restaurant",
    payment_method: "stripe",
    promo_code: "",
    notes: "",
  });

  useEffect(() => {
    if (user && user.role === "customer") {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        customer_phone: f.customer_phone || user.phone || "",
      }));
    }
  }, [user]);

  const [appliedPromo, setAppliedPromo] = useState(null);

  const deliveryFee = 0; // pickup only

  const discount = appliedPromo?.discount || 0;
  const total = Math.max(0, subtotal + deliveryFee - discount);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validatePromo = async () => {
    if (!form.promo_code) return;
    try {
      const r = await api.post("/promo/validate", { code: form.promo_code, subtotal });
      setAppliedPromo(r.data);
      toast.success(`Code ${r.data.code} appliqué`, { description: `${formatFCFA(r.data.discount)} de réduction` });
    } catch (e) {
      setAppliedPromo(null);
      toast.error(e.response?.data?.detail || "Code invalide");
    }
  };

  const submit = async () => {
    if (!items.length) {
      toast.error("Votre panier est vide");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Create order (status=pending)
      const orderResp = await api.post("/orders", { ...form, items });
      const orderId = orderResp.data.id;
      // 2) Create Stripe Checkout session (server-side amount)
      const sessionResp = await api.post("/payments/checkout/session", {
        order_id: orderId,
        origin_url: window.location.origin,
      });
      if (!sessionResp.data?.url) throw new Error("No checkout URL");
      // Clear cart only after we know Stripe URL — the order has been persisted
      clear();
      // 3) Redirect to Stripe-hosted checkout
      window.location.href = sessionResp.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du paiement", { duration: 8000 });
      setSubmitting(false);
    }
  };

  const minAmountUSD = 0.50;
  const belowMin = total < minAmountUSD;

  const canNext = () => {
    if (step === 1) return form.customer_name && form.customer_email && form.customer_phone;
    if (step === 2) return !!form.delivery_method;
    return true;
  };

  if (items.length === 0) {
    return (
      <div className="bg-white text-[var(--dsf-text)] min-h-screen">
        <Header />
        <div className="pt-32 px-6 text-center text-[var(--dsf-text-secondary)]">
          <p className="mb-4">Votre panier est vide.</p>
          <button onClick={() => navigate("/")} className="btn-primary">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-20" data-testid="checkout-page">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] mb-2">Finaliser ma commande</h1>
          <p className="text-[var(--dsf-text-muted)] mb-10">Pas besoin de compte · Paiement 100% sécurisé · Simulé en environnement de démonstration</p>

          {/* Stepper */}
          <div className="flex items-center gap-3 mb-10 flex-wrap">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center text-sm font-semibold ${
                  step >= s.id ? "dd-bg-bone text-[var(--dd-green)]" : "bg-transparent text-[var(--dsf-text-muted)]"
                }`}>
                  {step > s.id ? <Check size={14} /> : s.id}
                </div>
                <span className={`text-sm uppercase tracking-wider font-medium ${step >= s.id ? "text-[var(--dd-green)]" : "text-[var(--dsf-text-muted)]"}`}>
                  {s.label}
                </span>
                {i < STEPS.length - 1 && <div className="w-8 h-px bg-[var(--dsf-border)] mx-2" />}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              {step === 1 && (
                <div className="space-y-5" data-testid="step-contact">
                  <Field label="Nom complet" value={form.customer_name} onChange={(v) => update("customer_name", v)} testid="input-name" />
                  <Field label="Email" type="email" value={form.customer_email} onChange={(v) => update("customer_email", v)} testid="input-email" />
                  <Field label="Téléphone" value={form.customer_phone} onChange={(v) => update("customer_phone", v)} testid="input-phone" />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="step-delivery">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] mb-3 font-bold">Choisissez votre lieu de ramassage</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { v: "pickup_restaurant", icon: Store, name: deliveryCfg.restaurant_name, addr: deliveryCfg.restaurant_address, phone: deliveryCfg.restaurant_phone, hours: deliveryCfg.restaurant_hours, accent: "from-[var(--dd-green)] to-[var(--dd-green-deep)]" },
                        { v: "pickup_foodtruck", icon: Truck, name: deliveryCfg.foodtruck_name, addr: deliveryCfg.foodtruck_address, phone: deliveryCfg.foodtruck_phone, hours: deliveryCfg.foodtruck_hours, accent: "from-[var(--dd-coral)] to-[var(--dd-mustard-dark)]" },
                      ].map((o) => {
                        const Icon = o.icon;
                        const active = form.delivery_method === o.v;
                        return (
                          <button
                            key={o.v}
                            onClick={() => update("delivery_method", o.v)}
                            className={`p-5 text-left border-2 rounded-2xl transition-all ${active ? "border-[var(--dd-green)] bg-[var(--dd-green)]/5" : "border-black/5 hover:border-[var(--dd-green)]/40"}`}
                            data-testid={`delivery-${o.v}`}
                          >
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${o.accent} text-white flex items-center justify-center mb-3`}>
                              <Icon size={20} />
                            </div>
                            <div className="font-serif text-lg font-semibold text-[var(--dd-green)]">{o.name}</div>
                            <div className="text-xs text-[var(--dsf-text-muted)] mt-2 flex items-start gap-1.5"><MapPin size={11} className="mt-0.5 shrink-0" /> {o.addr}</div>
                            <div className="text-xs text-[var(--dsf-text-muted)] mt-1">📞 {o.phone}</div>
                            <div className="text-xs text-[var(--dsf-text-muted)] mt-1">🕐 {o.hours}</div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--dsf-success)] mt-3">Gratuit · prêt en 15 min</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block mb-2">Note (optionnel)</label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      className="w-full border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)]"
                      rows={3}
                      data-testid="input-notes"
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6" data-testid="step-payment">
                  <div className="border border-[var(--dd-green)]/20 bg-white p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <CreditCard size={22} className="text-[var(--dd-green)]" />
                      <div className="font-serif text-xl text-[var(--dd-green)]">Paiement sécurisé par Stripe</div>
                    </div>
                    <div className="text-sm text-[var(--dsf-text-muted)] leading-relaxed">
                      Cliquez sur « Confirmer & payer » pour être redirigé(e) vers la page de paiement sécurisée Stripe. Vous pouvez payer par carte Visa, Mastercard, American Express, Apple Pay ou Google Pay. Aucune information bancaire n'est stockée sur notre serveur.
                    </div>
                    <div className="flex items-center gap-3 mt-4 text-[10px] uppercase tracking-wider text-[var(--dsf-text-muted)] font-bold">
                      <span className="px-2 py-1 bg-[var(--dd-bone)] rounded">SSL</span>
                      <span className="px-2 py-1 bg-[var(--dd-bone)] rounded">PCI DSS</span>
                      <span className="px-2 py-1 bg-[var(--dd-bone)] rounded">3D Secure</span>
                    </div>
                  </div>

                  <div className="border border-black/5 p-5 bg-white">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] mb-3">Code promo</div>
                    <div className="flex gap-2">
                      <input
                        value={form.promo_code}
                        onChange={(e) => update("promo_code", e.target.value)}
                        placeholder="BIENVENUE10"
                        className="flex-1 border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)]"
                        data-testid="input-promo"
                      />
                      <button onClick={validatePromo} className="btn-outline-dark" data-testid="apply-promo-button">Appliquer</button>
                    </div>
                    {appliedPromo && (
                      <div className="text-xs text-[var(--dsf-success)] mt-2" data-testid="promo-success">
                        ✓ Code appliqué — {formatFCFA(appliedPromo.discount)} de réduction
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-10">
                {step > 1 ? (
                  <button onClick={() => setStep(step - 1)} className="btn-outline-dark" data-testid="prev-step-button">
                    Retour
                  </button>
                ) : <div />}
                {step < 3 ? (
                  <button
                    onClick={() => canNext() && setStep(step + 1)}
                    disabled={!canNext()}
                    className="btn-primary disabled:opacity-50"
                    data-testid="next-step-button"
                  >
                    Continuer
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting || belowMin} className="btn-primary disabled:opacity-50" data-testid="place-order-button">
                    {submitting ? "Traitement…" : belowMin ? `Minimum $${minAmountUSD.toFixed(2)}` : "Confirmer et payer"}
                  </button>
                )}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 card-light p-6">
                <h2 className="font-serif text-2xl text-[var(--dd-green)] mb-5">Votre commande</h2>
                <div className="space-y-4 max-h-72 overflow-y-auto pb-4 border-b border-black/5">
                  {items.map((it, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <img src={resolveImage(it.image)} alt={it.name} className="w-14 h-14 object-cover product-img-wrap" />
                      <div className="flex-1">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-[var(--dsf-text-muted)]">x{it.quantity}</div>
                      </div>
                      <div className="font-medium">{formatFCFA(it.price * it.quantity)}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 text-sm py-4 border-b border-black/5">
                  <div className="flex justify-between"><span>Sous-total</span><span>{formatFCFA(subtotal)}</span></div>
                  <div className="flex justify-between"><span>Ramassage en boutique</span><span>Gratuit</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between text-[var(--dsf-success)]"><span>Code promo</span><span>-{formatFCFA(discount)}</span></div>
                  )}
                </div>
                <div className="flex justify-between items-baseline pt-4">
                  <span className="font-serif text-lg">Total</span>
                  <span className="font-serif text-2xl font-semibold text-[var(--dd-green)]" data-testid="checkout-total">{formatFCFA(total)}</span>
                </div>
                {belowMin && (
                  <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900" data-testid="below-min-warning">
                    ⚠️ <strong>Montant trop bas pour le paiement par carte.</strong> Stripe exige un minimum de ${minAmountUSD.toFixed(2)}. Ajoutez un autre article pour pouvoir payer.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-black/5 px-4 py-3 text-sm focus:border-[var(--dsf-green)] bg-white"
      data-testid={testid}
    />
  </div>
);
